import { useCallback, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTaskStorage } from '../../hooks/useTaskStorage';
import { v4 as uuidv4 } from 'uuid';
import type { FormProps } from '../components/CallTaskWizard';

export type TaskWizardFormData = FormProps & {
  contacts: Array<{
    name: string;
    phone: string;
  }>;
};

export const useTaskWizard = () => {
  const history = useHistory();
  const { params } = useRouteMatch<{ task_id: string }>();
  const {
    saveTask,
    saveDraft,
    getDraftById,
    getTaskById,
    removeDraft,
    updateDraft,
  } = useTaskStorage();
  const [formId, setFormId] = useState(1);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState<TaskWizardFormData>({
    taskName: '',
    language: '',
    callAgentName: '',
    agentCallInstruction: '',
    file: null,
    maxCallDuration: 5,
    engageConversation: true,
    leaveVoiceMail: true,
    keepTryingReachPerson: true,
    retriesNumber: 3,
    retryInterval: 60,
    timezone: 'Etc/GMT',
    taskDate: '',
    startTime: '',
    endTime: '',
    outboundPhoneNumber: '',
    contacts: [],
  });

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        if (params?.task_id) {
          const draft = getDraftById(params?.task_id);
          if (draft?.formData) {
            setFormData(draft.formData);
            setCurrentStep(draft.currentStep || 0);
            setFormId(f => f + 1);
            setIsLoading(false);
            return;
          }

          const task = getTaskById(params?.task_id);
          if (task?.formData) {
            setFormData(task.formData);
            setCurrentStep(task.currentStep || 0);
            setFormId(f => f + 1);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [params?.task_id, getDraftById, getTaskById]);

  const handleFormChange = useCallback(
    (newData: Partial<TaskWizardFormData>) => {
      const updatedData = { ...formData, ...newData };
      setFormData(updatedData);
    },
    [formData]
  );

  const saveAsDraft = useCallback(() => {
    const draftTask = {
      task_id: params?.task_id || uuidv4(),
      createdAt: new Date().toISOString(),
      createdBy: 'Current User',
      status: 'Draft' as const,
      contactsCount: formData.file?.length || 0,
      taskName: formData.taskName || 'Untitled Task',
      formData,
      currentStep,
    };

    if (params?.task_id) {
      updateDraft(draftTask);
    } else {
      saveDraft(draftTask);
    }
    history.push('/batch_calls');
  }, [formData, params?.task_id, saveDraft, updateDraft, history, currentStep]);

  const saveAsTask = useCallback(() => {
    const task = {
      task_id: params?.task_id || uuidv4(),
      createdAt: new Date().toISOString(),
      createdBy: 'Current User',
      status: 'Pending' as const,
      contactsCount: formData.file?.length || 0,
      taskName: formData.taskName,
      formData,
      currentStep,
    };

    removeDraft(params.task_id);
    saveTask(task);
    history.push('/batch_calls');
  }, [formData, params.task_id, saveTask, removeDraft, history, currentStep]);

  return {
    formData,
    handleFormChange,
    saveAsDraft,
    saveAsTask,
    formId,
    currentStep,
    setCurrentStep,
    isLoading,
  };
};
